import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Calendar, Clock, ArrowRight, Search } from "lucide-react";

import Navbar from "@/components/common/Navbar";
import Footer from "@/components/home/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { blogPosts } from "@/data/blogData";
import { cn } from "@/lib/utils";

const Blog = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("All");

  // Get featured post (most recent)
  const featuredPost = blogPosts[0];

  // Get remaining posts
  const remainingPosts = blogPosts.slice(1);

  // Filter posts based on search query and category
  const filteredPosts = remainingPosts.filter(post => {
    const matchesSearch = searchQuery === "" ||
      post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesCategory = activeCategory === "All" ||
      post.categories.includes(activeCategory);

    return matchesSearch && matchesCategory;
  });

  useEffect(() => {
    // Simulate loading delay for demo purposes
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 800);

    return () => clearTimeout(timer);
  }, []);

  const categories = ["All", "Wildlife", "Conservation", "Safari Tips", "Cultural", "Photography"];

  return (
    <div className="bg-white min-h-screen">
      {/* Hero Section with Image Background - similar to WildlifeViewing.tsx */}
      <div className="relative">
        <div className="absolute inset-0 overflow-hidden" style={{ height: "500px" }}>
          <img
            src="/Blog Imgages/Bloghero.jpg"
            alt="Rion Wildlife Safaris Blog"
            className="absolute inset-0 w-full h-full object-cover"
            fetchPriority="high"
          />
          <div className="absolute inset-0 bg-black/50 pointer-events-none"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 to-transparent pointer-events-none"></div>
        </div>
        <div className="relative">
          <Navbar />
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-32 flex flex-col items-center justify-center text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-white max-w-4xl leading-tight font-['Athelas',_serif] animate-fade-up">Safari Journal</h1>
            <div className="mt-8 animate-fade-up animation-delay-200">
              <Button className="bg-[#C05B2A] hover:bg-[#A04B1A] text-white px-8 py-6 rounded-md text-lg font-medium">EXPLORE ARTICLES</Button>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 w-full h-16 bg-gradient-to-t from-amber-50 to-transparent"></div>
      </div>

      {/* Safari Journal Content Section - This is the section that was previously in hero position */}
      <div className="bg-amber-50 border-b border-amber-100 py-16 relative z-10">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
              <div className="lg:col-span-5 animate-fade-up">
                <h2 className="text-4xl sm:text-5xl font-display font-bold text-gray-900 mb-6">
                  Safari Journal
                </h2>

                <div className="h-1 w-24 bg-amber-500 mb-6"></div>

                <p className="text-lg text-gray-700 mb-8">
                  Learn about wildlife conservation, safari experiences, and expert insights from Professor Rion Haraeb's 20+ years of guiding tours across Namibia, Botswana, and Victoria Falls.
                </p>

                <div className="flex flex-col sm:flex-row gap-3 mb-8 animate-fade-up animation-delay-200">
                  <div className="relative flex-grow">
                    <Input
                      type="search"
                      placeholder="Search articles..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10 pr-4 py-2 h-12 bg-white border border-amber-200 rounded-lg text-gray-900 placeholder:text-gray-500 w-full"
                    />
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-amber-700" />
                  </div>

                  <Button className="h-12 px-6 bg-amber-700 hover:bg-amber-800 text-white border-0">
                    Search
                  </Button>
                </div>

                <div className="flex flex-wrap gap-2 animate-fade-up animation-delay-300">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => setActiveCategory(category)}
                      className={cn(
                        "px-4 py-2 rounded-full text-sm font-medium transition-all",
                        activeCategory === category
                          ? "bg-amber-700 text-white"
                          : "bg-white text-gray-700 hover:bg-amber-100"
                      )}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              <div className="lg:col-span-7 animate-fade-up animation-delay-400">
                {/* Featured Post Card */}
                <Link to={`/blog/${featuredPost.slug}`} className="block">
                  <div className="bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                    <div className="relative h-80 aspect-video md:aspect-[16/9]">
                      <img
                        src={featuredPost.imageUrl}
                        alt={featuredPost.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                      <div className="absolute bottom-0 left-0 p-6 text-white">
                        <div className="flex items-center gap-4 mb-3">
                          <span className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                            Featured
                          </span>
                          <span className="flex items-center text-sm">
                            <Calendar className="h-4 w-4 mr-1" />
                            {featuredPost.date}
                          </span>
                        </div>
                        <h3 className="text-2xl font-bold mb-2">{featuredPost.title}</h3>
                        <p className="text-white/80 line-clamp-2">{featuredPost.excerpt}</p>
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Posts Grid */}
      <div className="bg-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl font-bold mb-12 text-gray-900 animate-fade-up">Latest Articles</h2>

            {isLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-gray-100 rounded-xl overflow-hidden animate-pulse">
                    <div className="h-48 bg-gray-200"></div>
                    <div className="p-6 space-y-4">
                      <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                      <div className="h-8 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-full"></div>
                      <div className="h-4 bg-gray-200 rounded w-2/3"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post, index) => (
                  <Link
                    key={post.id}
                    to={`/blog/${post.slug}`}
                    className="bg-white rounded-xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 flex flex-col h-full animate-fade-up"
                    style={{ animationDelay: `${(index % 3) * 100 + 200}ms` }}
                  >
                    <div className="relative h-48 aspect-video">
                      <img
                        src={post.imageUrl}
                        alt={post.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                      {post.categories.length > 0 && (
                        <div className="absolute top-4 left-4">
                          <span className="bg-amber-600 text-white text-xs font-bold px-3 py-1 rounded-full uppercase">
                            {post.categories[0]}
                          </span>
                        </div>
                      )}
                    </div>
                    <div className="p-6 flex-grow flex flex-col">
                      <div className="flex items-center text-sm text-gray-500 mb-3 gap-4">
                        <span className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {post.date}
                        </span>
                        <span className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {post.readTime} min read
                        </span>
                      </div>
                      <h3 className="text-xl font-bold mb-3 text-gray-900">{post.title}</h3>
                      <p className="text-gray-600 mb-4 line-clamp-3 flex-grow">{post.excerpt}</p>
                      <div className="flex items-center text-amber-700 font-medium mt-auto">
                        <span>Read more</span>
                        <ArrowRight className="ml-2 h-4 w-4" />
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-16 animate-fade-up">
                <h3 className="text-2xl font-bold mb-4 text-gray-700">No articles found</h3>
                <p className="text-gray-600 mb-8">Try adjusting your search or category filter</p>
                <Button
                  onClick={() => {
                    setSearchQuery("");
                    setActiveCategory("All");
                  }}
                  className="bg-amber-700 hover:bg-amber-800 text-white"
                >
                  Reset Filters
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Blog; 